import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import { IMPACT_LABELS } from "@server/impact/schema";
import { itypeFor, priorityFor } from "@server/rules/jira";
import { TICKET_TYPES, type TicketType } from "@server/submissions/types";
import type { DuplicateTicket } from "@server/triage/schema";
import { ApiError, useAcknowledge, useClose, useOverride, useRetry, useSetTicketType, useSubmission, useTeamFor, useTicketsChannel } from "../api/client";
import type { SubmissionView } from "../api/types";
import { Button, ErrorBanner, Field, Select, SeverityPill } from "../components/ui";
import { Workspace, type StepKey } from "../components/Workspace";
import { clearDraft } from "../draft";

const messageOf = (error: unknown): string | undefined =>
  error instanceof ApiError ? error.message : error ? "Something went wrong. Please try again." : undefined;

const CUSTOMER_FIX_NOTE =
  "You changed the type to Customer Fix — these are situation-specific interventions and always go to the team, no bug verdict required.";

const NEXT_STEP: Record<string, string> = {
  raise_in_huddle:
    "Post this in #cs-question-huddle — a teammate may be able to answer it directly, or help confirm whether it does need a ticket.",
  comment_on_existing: "This may already be tracked. Add your details as a comment on the existing Jira ticket.",
  add_more_detail: "Add more detail about what you expected and what happened, then submit again.",
  none: "If you still believe this needs engineering attention, you can override the triage and raise it anyway — it'll be logged as an escalation.",
};

export function SubmissionDetail() {
  const { id = "" } = useParams();
  const query = useSubmission(id);

  if (query.isPending) {
    return (
      <Workspace step="triage">
        <div className="phase-head">
          <h1>Loading…</h1>
        </div>
      </Workspace>
    );
  }
  if (query.isError || !query.data) {
    const missing = query.error instanceof ApiError && query.error.status === 404;
    return (
      <Workspace step="result">
        <ErrorBanner>{missing ? "We couldn't find that submission." : "We couldn't load this submission."}</ErrorBanner>
      </Workspace>
    );
  }
  return <Outcome view={query.data} />;
}

const VERDICT_STEP = "Reaching a verdict";
// Constants, so the timer effect is not restarted by every render.
const DEFAULT_PACE_MS = [1400];
const TRIAGE_PACE_MS = [2000, 10_000];
const SLOW_AFTER_MS = 20_000;

interface ProcessingProps {
  heading: string;
  sub: string;
  // Used when nothing real is known: the rows advance on a timer, and the last one waits for the answer.
  items: string[];
  // How long each timed row stays before the next one, in milliseconds; the last value repeats.
  pacesMs?: number[];
  // What the AI has really done so far. When present it replaces the timer.
  live?: { label: string; count: number }[];
  // Shown once the wait gets long.
  slowNote?: string;
}

function Processing({ heading, sub, items, pacesMs = DEFAULT_PACE_MS, live, slowNote }: ProcessingProps) {
  const [ticks, setTicks] = useState(0);
  const [slow, setSlow] = useState(false);
  const real = live !== undefined && live.length > 0;

  useEffect(() => {
    if (real) {
      return undefined;
    }
    if (ticks >= items.length - 1) {
      return undefined;
    }
    const timer = setTimeout(() => setTicks(ticks + 1), pacesMs[Math.min(ticks, pacesMs.length - 1)]);
    return () => clearTimeout(timer);
  }, [real, ticks, items.length, pacesMs]);

  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  // Real rows: reading the details is done by the time anything else happens, and the AI is on the latest check.
  const rows = real ? [items[0] ?? "Reading your ticket details", ...live.map((step) => (step.count > 1 ? `${step.label} · ${step.count} lookups` : step.label)), VERDICT_STEP] : items;
  const active = real ? rows.length - 2 : ticks;

  return (
    <div className="phase-triage">
      <div className="phase-head">
        <h1>{heading}</h1>
        <p>{sub}</p>
      </div>
      <div className="triage-visual">
        <div className="triage-bar">
          <div className="triage-bar-fill" style={{ width: `${Math.round(((active + 0.5) / rows.length) * 100)}%` }} />
        </div>
      </div>
      <ul className="checklist">
        {rows.map((label, index) => (
          <li key={label} className={index < active ? "done" : index === active ? "active" : ""}>
            <span className="tick">✓</span>
            {label}
          </li>
        ))}
      </ul>
      {slow && slowNote && <p className="record-note">{slowNote}</p>}
    </div>
  );
}

function Summary({ view }: { view: SubmissionView }) {
  const { form } = view;
  const routedTeam = useTeamFor(form.feature, view.triage?.result?.team);
  const rows: [string, ReactNode][] = [
    ["Ticket Type", view.ticketType],
    ["Raised in huddle?", form.huddle],
    ["Company Name", form.companyName],
    ["Business Unit", form.businessUnit],
    ["Pricing Plan", form.pricingPlan],
    ["Feature", form.feature ?? "—"],
    ["Severity", <SeverityPill key="sev" severity={form.severity} />],
    ["Routed team", routedTeam],
  ];
  return (
    <div className="record-summary">
      <h2>What you submitted</h2>
      <dl className="field-grid">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
        <div className="details-wrap">
          <dt>Details</dt>
          <dd className="details-cell">{form.details}</dd>
        </div>
      </dl>
    </div>
  );
}

// Lets the person correct the type triage chose before the ticket is raised. Changes are recorded.
function TypePicker({ view }: { view: SubmissionView }) {
  const setType = useSetTicketType();
  const source = view.ticketTypeCorrected
    ? `You changed this from ${view.aiTicketType}.`
    : view.aiTicketType
      ? "Classified by AI triage."
      : "Triage was unavailable, so this is a default.";
  return (
    <div className="issue-form">
      <Field id="ticket-type" label="Ticket type" hint={`${source} Change it if it's wrong; it helps us improve triage.`} error={messageOf(setType.error)}>
        <Select
          id="ticket-type"
          required
          value={view.ticketType}
          disabled={setType.isPending}
          options={TICKET_TYPES}
          onChange={(e) => setType.mutate({ id: view.id, variables: { ticketType: e.target.value as TicketType } })}
        />
      </Field>
    </div>
  );
}

function Verdict({ title, reasons, warn }: { title: string; reasons: string[]; warn?: boolean }) {
  return (
    <div className={`verdict-card${warn ? " verdict-card-warn" : ""}`}>
      <h2>{title}</h2>
      <ul>
        {reasons.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    </div>
  );
}

function PossibleDuplicates({ duplicates, label = "Possible duplicate in Jira" }: { duplicates: DuplicateTicket[]; label?: string }) {
  return (
    <>
      {duplicates.map((duplicate) => (
        <p key={duplicate.listItemId}>
          {label}:{" "}
          {duplicate.url ? (
            <a href={duplicate.url} target="_blank" rel="noreferrer">
              {duplicate.listItemId}
            </a>
          ) : (
            <code>{duplicate.listItemId}</code>
          )}
          {duplicate.summary ? ` ${duplicate.summary}` : ""}
          {duplicate.status ? ` (${duplicate.status})` : ""} — {duplicate.why}
        </p>
      ))}
    </>
  );
}

function ImpactCard({ view }: { view: SubmissionView }) {
  const impact = view.ticketType === "Bug" ? view.triage?.result?.impact : undefined;
  if (!impact) {
    return null;
  }
  const wider = impact.verdict === "possibly_wider" || impact.verdict === "confirmed_wider";
  const count = typeof impact.affectedCompanies === "number" ? ` · ${Math.round(impact.affectedCompanies)} companies` : "";
  return (
    <Verdict
      warn={wider || impact.verdict === "unable_to_verify"}
      title={`Wider impact: ${IMPACT_LABELS[impact.verdict]}${count}`}
      reasons={[impact.summary, ...(wider && impact.faultyLogic ? [`Faulty logic: ${impact.faultyLogic}`] : [])].filter(Boolean)}
    />
  );
}

function Outcome({ view }: { view: SubmissionView }) {
  const channel = useTicketsChannel();
  const working = view.status === "TRIAGING" || view.status === "TICKET_QUEUED";
  const step: StepKey = working ? "triage" : "result";

  return (
    <Workspace step={step}>
      {view.status === "TRIAGING" && (
        <Processing
          heading="Triaging your issue…"
          sub="No engineer is notified until this finishes."
          items={["Reading your ticket details", "Checking Vestd data, similar tickets and the knowledge base", VERDICT_STEP]}
          pacesMs={TRIAGE_PACE_MS}
          live={view.progress?.steps}
          slowNote="Still working. Checking the code or other tickets can take a few minutes. This page updates by itself when it's done."
        />
      )}
      {view.status === "TICKET_QUEUED" && (
        <Processing
          heading="Creating your ticket…"
          sub="Almost there — this takes a few seconds."
          items={["Adding it to the ENG Tickets Tracker", "Creating the Jira ticket", `Posting to ${channel}`]}
        />
      )}
      {view.status === "PASSED" && <Passed view={view} />}
      {view.status === "FLAGGED" && (view.ticketType === "Customer Fix" ? <Passed view={view} /> : <Flagged view={view} />)}
      {view.status === "TICKET_CREATED" && <Created view={view} />}
      {view.status === "TICKET_FAILED" && <Failed view={view} />}
      {view.status === "CLOSED_NO_TICKET" && <Closed />}
    </Workspace>
  );
}

function Passed({ view }: { view: SubmissionView }) {
  const navigate = useNavigate();
  const channel = useTicketsChannel();
  const acknowledge = useAcknowledge();
  const close = useClose();
  const failedOpen = view.triage?.failedOpen;
  // Triage said no ticket, but the person changed the type to Customer Fix.
  const reclassified = view.status === "FLAGGED";
  const customerFix = view.ticketType === "Customer Fix";
  const team = useTeamFor(view.form.feature, view.triage?.result?.team);
  const error = messageOf(acknowledge.error) ?? messageOf(close.error);

  return (
    <div className="result">
      <div className={`result-badge ${failedOpen ? "result-badge-warn" : "result-badge-good"}`}>
        {failedOpen ? "Triage unavailable" : customerFix ? "✓ Customer Fix" : "✓ Platform issue"}
      </div>
      <h1>{failedOpen ? "We couldn't run triage just now" : customerFix ? "This is a Customer Fix" : "This looks like a platform issue"}</h1>
      <p>
        Raising it creates an engineering ticket and posts it to <strong>{channel}</strong>, routed to <strong>{team}</strong>.
      </p>
      <Verdict
        title={failedOpen ? "Why you can still raise it" : reclassified ? "Why this goes to the team" : "Why triage passed this"}
        reasons={
          failedOpen
            ? ["Triage was unavailable, so this wasn't checked by AI. You can still raise the ticket."]
            : reclassified
              ? [CUSTOMER_FIX_NOTE]
              : (view.triage?.result?.reasons ?? [])
        }
      />
      {!failedOpen && !reclassified && <ImpactCard view={view} />}
      {!failedOpen && customerFix && view.triage?.result && view.triage.result.duplicates.length > 0 && (
        <div className="verdict-card">
          <h2>This may already be requested</h2>
          <PossibleDuplicates duplicates={view.triage.result.duplicates} label="Existing Jira ticket" />
        </div>
      )}
      <TypePicker view={view} />
      <Summary view={view} />
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <div className="result-actions">
        <Button disabled={acknowledge.isPending} onClick={() => acknowledge.mutate({ id: view.id, variables: undefined })}>
          Raise the ticket
        </Button>
        <Button
          variant="secondary"
          disabled={close.isPending}
          onClick={() => close.mutate({ id: view.id, variables: undefined }, { onSuccess: () => void navigate("/new/eng") })}
        >
          Edit submission
        </Button>
        <Button variant="secondary" disabled={close.isPending} onClick={() => close.mutate({ id: view.id, variables: undefined }, { onSuccess: clearDraft })}>
          Close without raising
        </Button>
      </div>
    </div>
  );
}

function Flagged({ view }: { view: SubmissionView }) {
  const navigate = useNavigate();
  const override = useOverride();
  const close = useClose();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const result = view.triage?.result;
  const tooShort = reason.trim().length < 10;
  const error = messageOf(override.error) ?? messageOf(close.error);

  return (
    <div className="result">
      <div className="result-badge result-badge-warn">Not flagged as an engineering issue</div>
      <h1>No ticket was created</h1>
      <p>Based on what you've described, an engineering ticket is not required. Here is the reason why:</p>
      <Verdict warn title="Why triage flagged this" reasons={result?.reasons ?? []} />

      <div className="next-steps">
        <h2>Suggested next step</h2>
        <p>{NEXT_STEP[result?.nextStep ?? "none"]}</p>
        {result && <PossibleDuplicates duplicates={result.duplicates} />}
      </div>

      <TypePicker view={view} />
      <Summary view={view} />
      <p className="record-note">No ticket is created unless you override below.</p>

      {asking && (
        <div className="issue-form">
          <Field id="override-reason" label="Why does this still need engineering?" hint="At least 10 characters. It's added to the ticket for the team.">
            <textarea id="override-reason" value={reason} maxLength={2000} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
      )}
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <div className="result-actions">
        {asking ? (
          <>
            <Button disabled={tooShort || override.isPending} onClick={() => override.mutate({ id: view.id, variables: { reason: reason.trim() } })}>
              Raise the ticket anyway
            </Button>
            <Button variant="secondary" onClick={() => setAsking(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setAsking(true)}>Raise the ticket anyway</Button>
            <Button
              variant="secondary"
              disabled={close.isPending}
              onClick={() => close.mutate({ id: view.id, variables: undefined }, { onSuccess: () => void navigate("/new/eng") })}
            >
              Edit submission
            </Button>
            <Button variant="secondary" disabled={close.isPending} onClick={() => close.mutate({ id: view.id, variables: undefined }, { onSuccess: clearDraft })}>
              Close without raising
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

const timeOf = (iso: string): string => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

function Created({ view }: { view: SubmissionView }) {
  // The ticket exists now, so the next issue starts from an empty form.
  useEffect(() => clearDraft(), []);
  const navigate = useNavigate();
  const channel = useTicketsChannel();
  const { form } = view;
  const team = useTeamFor(form.feature, view.triage?.result?.team);
  const jira = view.ticket?.steps?.jira?.value;
  const item = view.ticket?.steps?.listItem?.value;
  // Triage said no ticket, but the person changed the type to Customer Fix.
  const reclassified = view.ticketTypeCorrected && view.triage?.result?.verdict === "not_needed";
  const reasons = view.triage?.failedOpen
    ? ["Triage was unavailable, so this was raised without an AI check."]
    : reclassified
      ? [CUSTOMER_FIX_NOTE]
      : (view.triage?.result?.reasons ?? []);

  return (
    <div className="result">
      {view.override && <span className="escalated-note">⤴ Escalated manually after AI triage flagged it</span>}
      <div className="result-badge result-badge-good">
        ✓ {view.override ? "Raised by override" : view.ticketType === "Customer Fix" ? "Customer Fix" : "Platform issue"}
      </div>
      <h1>Ticket created</h1>
      <p>
        This has been logged as an engineering ticket and posted to <strong>{channel}</strong>, routed to <strong>{team}</strong>.
      </p>
      <div className="tracker-prompt">
        <strong>Add comments and follow progress in the ENG Tickets Tracker.</strong>
        {item ? (
          <>
            {" "}
            <a href={item.url} target="_blank" rel="noreferrer">
              Open your ticket in the tracker
            </a>
          </>
        ) : (
          " Look for your ticket there by company name."
        )}
      </div>

      <div className="handoff">
        <h2>Where it went</h2>
        <p className="handoff-sub">This is the post and ticket that the team sees.</p>

        <div className="slack-preview">
          <div className="slack-preview-head">
            <span className="slack-avatar">🎫</span>
            <div>
              <span className="slack-bot-name">
                Triage Desk<span className="slack-app-badge">APP</span>
              </span>
              <span className="slack-time">Today at {timeOf(view.statusChangedAt)} · {channel}</span>
            </div>
          </div>
          <p className="slack-line">
            Please go to <strong>ENG Tickets Tracker</strong> to add any comments to your ticket and track its progress.
          </p>
          {item && (
            <p className="slack-line">
              <strong>NEW ENG TICKET:</strong>{" "}
              <a className="slack-link" href={item.url} target="_blank" rel="noreferrer">
                {item.url}
              </a>
            </p>
          )}
          <dl className="slack-fields">
            <div>
              <dt>Company Name</dt>
              <dd>{form.companyName}</dd>
            </div>
            <div>
              <dt>Business Unit</dt>
              <dd>{form.businessUnit}</dd>
            </div>
            <div>
              <dt>Feature</dt>
              <dd>{form.feature ?? "—"}</dd>
            </div>
            <div>
              <dt>Severity</dt>
              <dd>
                <SeverityPill severity={form.severity} />
              </dd>
            </div>
          </dl>
          <p className="slack-detail-label">Ticket Details</p>
          <p className="slack-detail">{form.details}</p>

          {jira && (
            <div className="list-unfurl">
              <div className="list-unfurl-head">🎫 Jira · {jira.key}</div>
              <p className="list-unfurl-title">
                <a href={jira.url} target="_blank" rel="noreferrer">
                  PET - {view.ticket?.title}
                </a>
              </p>
              <div className="list-field-row">
                <span className="list-field-label">Issue type</span>
                <span className="cat-chips">
                  <span className="cat-chip">Task · {itypeFor(view.ticketType)}</span>
                </span>
              </div>
              <div className="list-field-row">
                <span className="list-field-label">Team</span>
                <span>{team}</span>
              </div>
              <div className="list-field-row">
                <span className="list-field-label">Priority</span>
                <span>
                  {priorityFor(form.severity)} (from Severity: {form.severity})
                </span>
              </div>
              <div className="list-field-row">
                <span className="list-field-label">Assignee</span>
                <span>{jira.assigned ? `${team} lead` : "Unassigned"}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <Verdict title={view.override ? "Original triage note" : "Why triage passed this"} reasons={reasons} />
      {!view.override && <ImpactCard view={view} />}
      {view.override && <p className="record-note">Your reason for raising it anyway: {view.override.reason}</p>}

      <div className="result-actions">
        <Button variant="secondary" onClick={() => void navigate("/new/eng")}>
          Raise another engineering ticket
        </Button>
      </div>
    </div>
  );
}

function Failed({ view }: { view: SubmissionView }) {
  const retry = useRetry();
  const error = messageOf(retry.error);
  return (
    <div className="result">
      <div className="result-badge result-badge-incident">Ticket not created</div>
      <h1>We couldn't create the ticket</h1>
      <p>Nothing was lost. You can try again, and engineering has been alerted.</p>
      {view.ticket?.lastError && <p className="mono-note">{view.ticket.lastError}</p>}
      <Summary view={view} />
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <div className="result-actions">
        <Button disabled={retry.isPending} onClick={() => retry.mutate({ id: view.id, variables: undefined })}>
          Try again
        </Button>
      </div>
    </div>
  );
}

function Closed() {
  const navigate = useNavigate();
  return (
    <div className="result">
      <div className="result-badge result-badge-warn">Closed</div>
      <h1>No ticket was created</h1>
      <p>This submission was closed without raising a ticket.</p>
      <div className="result-actions">
        <Button
          variant="secondary"
          onClick={() => {
            clearDraft();
            void navigate("/new/eng");
          }}
        >
          Raise another engineering ticket
        </Button>
      </div>
    </div>
  );
}
